import { DisaggregatedMetric } from "../services/types";

export const processData = (dataItem: DisaggregatedMetric, filterId?: number) => {
    const version = dataItem.versions[0];
    if (!version) return [];

    const tallyMap = new Map<string, number>();
    const rawValueMap = new Map<string, number | string>();

    const disaggregations = filterId 
    ? version.disaggregations.filter(d => d.id === filterId) 
    : version.disaggregations;

    disaggregations.forEach(d => {
        let percentage;
        let rawValue;

        switch (version.unit) {
            case 'integer':
                percentage = d.value / version.value;
                rawValue = d.value;
                break;
            case 'percentage':
                percentage = Math.min(d.value, 1);
                rawValue = d.percent_calc?.n ?? 0;
                break;
            case 'decimal':
                percentage = d.value;
                rawValue = d.value;
                break;
            case 'ratio':
                const n = d.percent_calc?.n;
                const dValue = d.percent_calc?.d;
                if (n === undefined || dValue === undefined || dValue === 0) {
                    percentage = 0;
                    rawValue = '0:0';
                } else {
                    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
                    const greatestCommonDivisor = gcd(n, dValue);
                    percentage = (n / greatestCommonDivisor) / (dValue / greatestCommonDivisor);
                    rawValue = `${n / greatestCommonDivisor}:${dValue / greatestCommonDivisor}`;
                }
                break;
            default:
                percentage = 0;
                rawValue = '0';
        }

        tallyMap.set(d.name.trim(), percentage);
        rawValueMap.set(d.name.trim(), rawValue);
    });

    return Array.from(tallyMap.entries()).map(([name, percentage]) => ({
        label: name,
        value: percentage,
        rawValue: rawValueMap.get(name) ?? 0,
    }));
};

// skipHistogram - array of metric ids to exclude from generating histogram in metadata modal
export const skipHistogram: number[] = [345, 347, 350, 351, 352, 353, 354, 355, 1180];

// noDataPlugin - plugin to display a 'no data available' message inside of a chart 
export const noDataPlugin = {
    id: 'noData',
    afterDatasetsDraw: (chart: any) => {
        const { ctx, data, chartArea, options } = chart;
        const { top, left, width, height } = chartArea;

        ctx.save();

        // Check if the datasets array is empty or if all data values are 0 or NaN (charts using percentages / 0)
        const noData = data.datasets.length === 0 || 
            data.datasets[0].data.every((value: any) => value === 0 || isNaN(value));

        if (noData) {
            // Hide axis labels dynamically
            if (options.scales) {
                options.scales.x.ticks.display = false;
                options.scales.y.ticks.display = false;
                options.scales.x.grid.drawTicks = false;
                options.scales.y.grid.drawTicks = false;
            }

            // style the no data available container
            ctx.fillStyle = '#E5E7EB';
            ctx.fillRect(left, top, width, height);

            ctx.font = 'bold 12px Inter, system-ui, Avenir, Helvetica, Arial, sans-serif';
            ctx.fillStyle = 'black';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('No Data Available', left + width / 2, top + height / 2);
        }
        ctx.restore();
    }
};

// Short alt text (used for aria-label on the canvas)
export function buildShortAlt(
    title: string,
    rows: { label: string; value: number; rawValue: number | string }[]
    ) {
    const items = rows.filter(r => r.label?.trim()); // drop your blank separator
    if (!items.length) return `${title}: no data.`;
    return `${title} — horizontal bar chart with ${items.length} categories.`;
}

// Long description (linked via aria-describedby)
export function buildLongDesc(
    title: string,
    rows: { label: string; value: number; rawValue: number | string }[]
    ) {
    const items = rows.filter(r => r.label?.trim());
    if (!items.length) return `${title}: no data to describe.`;

    const values = items.map(i => Number(i.rawValue));
    const total = values.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
    const maxIdx = values.indexOf(Math.max(...values));
    const minIdx = values.indexOf(Math.min(...values));
    const top = items[maxIdx];
    const bottom = items[minIdx];

    const bullets = items
        .map(i => `${i.label}: ${Number.isFinite(Number(i.rawValue)) ? i.rawValue : i.value}`)
        .join("; ");

    return [
        `${title}. This chart presents values for ${items.length} categories.`,
        `Highest: ${top.label} (${top.rawValue}). Lowest: ${bottom.label} (${bottom.rawValue}).`,
        `Total of all categories: ${total}.`,
        `Data by category — ${bullets}.`
    ].join(" ");
}

export const toPercent = (
    value: number | null | undefined,
    sigFigs?: number
): string => {
    if (value == null || isNaN(value)) return "";

    const decimals = sigFigs ?? 0;

    return `${(value * 100).toFixed(decimals)}%`;
};